import { SChip } from "@avalara/skylab-sdk/react";
import React from "react";
import { useDispatch } from "react-redux";
import { EcmsFiltersState, setHasFilterChips } from "../../app/ecmsSlice";

interface ECMSFilterChipProps {
    ecmsFiltersState: EcmsFiltersState;
    showFilters: boolean;
    onDismissChip: (key: string) => void;
}

type KeyLabelMapping = {
    [key: string]: string;
};

const KEY_LABEL_MAPPING: KeyLabelMapping = {
    companyId: "Avatax Company ID",
    customerName: "Customer Name",
    customerCode: "Customer Code",
    avaCertId: "CC Cert ID",
    exemptCertId: "ECMS Cert ID",
    country: "Issuing Country",
    region: "Regions Applicable",
};

function ECMSFilterChip({
    ecmsFiltersState,
    showFilters,
    onDismissChip,
}: ECMSFilterChipProps): React.ReactElement | null {
    const dispatch = useDispatch();

    if (!showFilters) {
        return null;
    }

    if (!ecmsFiltersState || Object.keys(ecmsFiltersState).length === 0) {
        return null;
    }

    const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) {
            return "";
        }
        if (Array.isArray(value)) {
            return value.join(", ");
        }
        if (typeof value === "object") {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const dismissChip = (key: string) => () => {
        onDismissChip(key);
    };

    const chips = Object.entries(ecmsFiltersState)
        .map(([key, value]): React.ReactElement | null => {
            // Handle array fields (country, region)
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return null;
                }
                const label = KEY_LABEL_MAPPING[key] || key;
                return (
                    <SChip key={key} onS-dismiss={dismissChip(key)}>
                        <span>
                            {label}: {value.length} {value.length === 1 ? "option" : "options"}
                        </span>
                    </SChip>
                );
            }

            // Handle string fields
            const displayValue = formatValue(value);
            if (!displayValue || displayValue.trim() === "") {
                return null;
            }
            const label = KEY_LABEL_MAPPING[key] || key;
            return (
                <SChip key={key} onS-dismiss={dismissChip(key)}>
                    <span>
                        {label}: {displayValue}
                    </span>
                </SChip>
            );
        })
        .filter((chip): chip is React.ReactElement => chip !== null);

    if (chips.length === 0) {
        dispatch(setHasFilterChips(false));
        return null;
    }
    dispatch(setHasFilterChips(true));

    return (
        <React.Fragment>
            <div>
                <span>Filtered by</span>
                {chips}
            </div>
        </React.Fragment>
    );
}

export default ECMSFilterChip;
